import { Grid as MaterialGrid, Typography } from '@material-ui/core';
import { Grid, GridColumn, GridToolbar } from '@progress/kendo-react-grid';
import React, { useEffect, useState } from 'react';
import { SimpleColumnMenu } from '../../../../libs/grid';
import useStylesGrid from '../../../../styles/grid.styles';
import LoadingPanel from '../../../../ui/old/Feedback/LoadingPanel';
import { useForceSync } from '../../hooks/useForceSync';
import useStyles from '../../styles/useStyles';
import { ActionConfirmationDialogComponent } from '../../ui-kit';
import useSecurity from './../../hooks/useSecurity/useSecurity';
import { DeleteDataCell } from './DeleteFolder';
import { ForceSyncButton } from './ForceSyncButton';
import { InfoDataChangesCell } from './InfoFolder';
import useConfigurationFoldersGrid from './useConfigurationFoldersGrid';
import { useDeleteConfigurationFolder } from '../../hooks/useConfigurationFolder';
import { State } from '@progress/kendo-data-query';
import { useToastify } from '../../../../Hooks';

interface ConfigurationFoldersGridProps {
    gridState: State;
    setGridState: React.Dispatch<React.SetStateAction<State>>;
    addFoldersInGrid: any;
}

const ConfigurationFoldersGrid: React.FC<ConfigurationFoldersGridProps> = ({
    gridState,
    setGridState,
    addFoldersInGrid,
}: any) => {
    const classesGrid = useStylesGrid();
    const classes = useStyles();
    const {
        ConfigurationFoldersQuery,
        ConfigurationFoldersQuery: { isLoading, isSuccess },
        gridResult,
        handleDataStateChange,
    } = useConfigurationFoldersGrid(gridState, setGridState);

    const [gridData, setGridData] = useState(gridResult);

    const [dataItem, setDataItem] = useState<any>({});

    const { canManage } = useSecurity();

    const { showToastSuccess, showToastError } = useToastify();

    const { handleOnClick, forceSyncDialogActive, setForceSyncDialogActive } = useForceSync();
    const { deleteFolderConfigurationMutation, visibleDeleteDialog, setVisibleDeleteDialog } =
        useDeleteConfigurationFolder();

    const handleOnClickForceSync = () => {
        const { folderId } = dataItem;
        handleOnClick(folderId);
    };

    const deleteRelaticsFolder = () => {
        const { folderId, folderName } = dataItem;
        deleteFolderConfigurationMutation.mutate(
            { folderId },
            {
                onSuccess: (result: any) => {
                    if (result.hasOwnProperty('Errors') && result?.Errors) {
                        const messgaesArray = result.Messages as any[];
                        showToastError(messgaesArray.map((x) => x.ErrorMessage).join('</br>'));
                    } else {
                        showToastSuccess(`${folderName} folder has been deleted successfully!`);
                        gridResult.data = gridResult.data
                            .slice(0, gridResult.data.indexOf(dataItem))
                            .concat(
                                gridResult.data.slice(gridResult.data.indexOf(dataItem) + 1, gridResult.data.length),
                            );
                        gridResult.total -= 1;
                        setGridData(gridResult);
                        setVisibleDeleteDialog(!visibleDeleteDialog);
                    }
                },
                onError: (err) => {
                    showToastError(`Error during delete of folder ${folderName}`);
                    setVisibleDeleteDialog(!visibleDeleteDialog);
                },
            },
        );
    };

    useEffect(() => {
        setGridData(gridResult);
    }, [gridResult]);

    useEffect(() => {
        ConfigurationFoldersQuery.refetch();
    }, [addFoldersInGrid]);

    return (
        <>
            <ActionConfirmationDialogComponent
                dialogActive={forceSyncDialogActive}
                setDialogActive={setForceSyncDialogActive}
                handleOnOk={handleOnClickForceSync}
                text={`Are you sure you want to start force sync for <b>${dataItem?.folderName}</b> ?`}
            />

            <ActionConfirmationDialogComponent
                dialogActive={visibleDeleteDialog}
                setDialogActive={setVisibleDeleteDialog}
                handleOnOk={deleteRelaticsFolder}
                text={`Are you sure you want to delete folder <b>${dataItem?.folderName}</b> ?`}
            />
            {isSuccess && (
                <Grid
                    className={classesGrid.grid + ' ' + classes.gridHeight}
                    pageable={{
                        buttonCount: 5,
                        type: 'numeric',
                        pageSizes: [5, 10, 20, 50],
                    }}
                    {...gridState}
                    {...gridData}
                    sortable={true}
                    onDataStateChange={handleDataStateChange}
                >
                    <GridToolbar>
                        <MaterialGrid container style={{ minHeight: '30px' }}>
                            <Typography variant="h6" style={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                                Configuration folders
                            </Typography>
                        </MaterialGrid>
                    </GridToolbar>
                    <GridColumn
                        field="workspaceName"
                        title="Workspace"
                        filterable={false}
                        headerClassName={classesGrid.gridColumnHeader}
                        className={classesGrid.gridColumn}
                        columnMenu={(props: any) => <SimpleColumnMenu {...props} />}
                        width={'250'}
                    />
                    <GridColumn
                        field="folderName"
                        title="Folder"
                        filterable={false}
                        headerClassName={classesGrid.gridColumnHeader}
                        className={classesGrid.gridColumn}
                        columnMenu={(props: any) => <SimpleColumnMenu {...props} />}
                        width={'320'}
                    />
                    <GridColumn
                        title="Info"
                        headerClassName={classesGrid.gridColumnHeader}
                        className={classesGrid.gridColumn}
                        sortable={false}
                        cell={(props: any) => <InfoDataChangesCell props={props} />}
                        width={'50'}
                    />
                    <GridColumn
                        field="fullPath"
                        title="Folder path"
                        headerClassName={classesGrid.gridColumnHeader}
                        className={classesGrid.gridColumn}
                        columnMenu={(props: any) => <SimpleColumnMenu {...props} />}
                        width={'auto'}
                    />
                    <GridColumn
                        field="lastSyncData"
                        title="Last sync on"
                        format="{0:dd/MM/yyyy HH:mm:ss}"
                        headerClassName={classesGrid.gridColumnHeader}
                        className={classesGrid.gridColumn}
                        width={'200'}
                    />

                    {canManage() ? (
                        <GridColumn
                            headerClassName={classesGrid.gridColumnHeader}
                            className={classesGrid.gridColumn}
                            sortable={false}
                            cell={(props: any) => (
                                <td className={props.className} style={{ textAlignLast: 'center' }}>
                                    <ForceSyncButton
                                        props={props}
                                        setDataItemForDelete={setDataItem}
                                        setVisible={setForceSyncDialogActive}
                                        visible={forceSyncDialogActive}
                                    />
                                    <DeleteDataCell
                                        props={props}
                                        setDataItemForDelete={setDataItem}
                                        setVisible={setVisibleDeleteDialog}
                                        visible={visibleDeleteDialog}
                                    />
                                </td>
                            )}
                            width={'100'}
                        />
                    ) : (
                        <GridColumn
                            field="empty"
                            headerClassName={classesGrid.gridColumnHeader}
                            className={classesGrid.gridColumn}
                            width={'0'}
                            cell={(props: any) => <td className={props.className}></td>}
                        />
                    )}
                </Grid>
            )}
            {isLoading && <LoadingPanel />}
        </>
    );
};

export default ConfigurationFoldersGrid;
